import { useEffect, useRef, useState } from 'react';
import { Camera, RefreshCw } from 'lucide-react';

// A live camera in a pop-up: the back camera if there is one, a big Take
// photo button, then Use photo / Retake. Works in any browser with a camera
// (computers too, where a file input's camera hint is ignored). onPhoto gets
// a JPEG Blob. If there's no camera or it's blocked, it says so and offers
// onFallback (choose a file instead).
export default function CameraCapture({ title = 'Take photo', onPhoto, onClose, onFallback }) {
  const video = useRef(null);
  const stream = useRef(null);
  const [error, setError] = useState(null);
  const [shot, setShot] = useState(null); // { blob, url }
  const [facing, setFacing] = useState('environment');

  useEffect(() => {
    let live = true;
    (async () => {
      setError(null);
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw new Error('none');
        const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: facing }, width: { ideal: 1920 }, height: { ideal: 1440 } }, audio: false });
        if (!live) { s.getTracks().forEach((t) => t.stop()); return; }
        stream.current = s;
        if (video.current) video.current.srcObject = s;
      } catch (e) {
        if (live) setError(e?.name === 'NotAllowedError' ? 'blocked' : 'none');
      }
    })();
    return () => {
      live = false;
      stream.current?.getTracks().forEach((t) => t.stop());
      stream.current = null;
    };
  }, [facing]);
  useEffect(() => () => { if (shot) URL.revokeObjectURL(shot.url); }, [shot]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const take = () => {
    const v = video.current;
    if (!v?.videoWidth) return;
    const c = document.createElement('canvas');
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0);
    c.toBlob((blob) => { if (blob) setShot({ blob, url: URL.createObjectURL(blob) }); }, 'image/jpeg', 0.85);
  };

  return (
    <div className="popup-backdrop camera-backdrop">
      <div className="popup camera-popup" role="dialog" aria-modal="true" aria-label={title}>
        <p className="popup-kicker">{title}</p>
        {error ? (
          <div className="camera-error">
            <p>{error === 'blocked' ? 'The camera is blocked for this app. Allow it in your browser or phone settings, or choose a photo instead.' : 'No camera found on this device. Choose a photo instead.'}</p>
            {onFallback && <button type="button" className="btn primary popup-btn" onClick={onFallback}>Choose a photo</button>}
          </div>
        ) : (
          <div className="camera-view">
            <video ref={video} autoPlay playsInline muted style={{ display: shot ? 'none' : 'block' }} />
            {shot && <img src={shot.url} alt="The photo you took" />}
          </div>
        )}
        {!error && !shot && (
          <div className="camera-actions">
            <button type="button" className="btn primary popup-btn with-icon" onClick={take}><Camera size={20} aria-hidden="true" /> Take photo</button>
            <button type="button" className="btn icon-only camera-flip" aria-label="Switch camera" onClick={() => setFacing((f) => (f === 'environment' ? 'user' : 'environment'))}><RefreshCw size={18} aria-hidden="true" /></button>
          </div>
        )}
        {shot && (
          <>
            <button type="button" className="btn primary popup-btn" onClick={() => { onPhoto(shot.blob); onClose(); }}>Use photo</button>
            <button type="button" className="btn popup-btn" onClick={() => setShot(null)}>Retake</button>
          </>
        )}
        <button type="button" className="btn popup-btn popup-cancel" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}
